import { Divider, makeStyles, mergeClasses, shorthands, tokens, Text } from '@fluentui/react-components';
import { FC, useMemo } from 'react';
import { IGptConfiguration } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import AppLogoWithTitle from '../Common/AppLogoWithTitle';
import { AppMenuContent } from './AppMenuContent';
import MenuCommands from './MenuCommands';
import { appConfig } from '../../AppConfig';
import AppMenuCollapsed from './AppMenuCollapsed';
import { useUserContext } from '../../Contexts/UserContext';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        boxSizing: 'border-box',
        backgroundColor: tokens.colorNeutralBackground1,
        width: '100%',
        position: 'relative', // For absolute positioning of collapsed content
        ...shorthands.overflow('hidden'),
    },
    rootExpanded: {
        ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
        alignItems: 'center',
    },
    rootCollapsed: {
        ...shorthands.padding(tokens.spacingVerticalM, '0'),
        alignItems: 'center',
    },
    content: {
        display: 'flex',
        flexGrow: 1,
        flexDirection: 'column',
        width: '100%',
        overflow: 'auto',
        minHeight: 0,
        flex: '1 1 auto',
    },
    expandedContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '465px'        
    },
    smallDivider: {
        flexGrow: 0,
        height: '1.5rem',
    },
});

interface AppMenuProps {
    onNewChat: (gptConfiguration: IGptConfiguration) => void;
    collapsed?: boolean;
}

const getEnvironmentLabel = (): string | undefined => {
    switch (appConfig.ENVIRONMENT) {
        case 'local':
            return 'Local';
        case 'development':
            return 'Development';
        case 'test':
            return 'Test';
        case 'preprod':
            return 'Pre-Prod';
        default:
            return undefined;
    }
};

const AppMenu: FC<AppMenuProps> = ({onNewChat, collapsed = false}) => {
    const classes = useStyles();
    const environmentLabel = getEnvironmentLabel();
    const { userProfile } = useUserContext();

    return (
        <div className={mergeClasses(classes.root, collapsed ? classes.rootCollapsed : classes.rootExpanded)}>
            {collapsed ? (
                <AppMenuCollapsed 
                    onNewChat={onNewChat} 
                    userName={userProfile?.name}
                    userPhoto={userProfile?.photo}
                />
            ) : (
                // Expanded state - show full content
                <>
                    <div className={classes.expandedContent}>
                        <AppLogoWithTitle theme='light' collapsed={false} />
                        {environmentLabel && <Text size={200} weight="semibold">{environmentLabel}</Text>}
                        <Divider className={classes.smallDivider} />
                    </div>
                    
                    <div className={classes.content}>
                        <MenuCommands onNewChat={onNewChat} showOnlyToggleMenuCommand={false} />
                        <AppMenuContent />
                    </div>
                </>
            )}
        </div>
    );
};

export default AppMenu;
